'use client';

import { useSyncExternalStore, type ReactNode } from 'react';
import { useQuality } from '@/components/shared/QualityProvider';
import { getIsIntroActive, subscribeIsIntroActive } from '@/lib/journeyState';

/**
 * Visibility gate around the mobile post-intro stack (project register
 * header + showcase + case studies). On low tier this wraps the children
 * in an opacity-0 layer until IntroLogoBreak releases `isIntroActive`,
 * then eases them in together — no piecemeal pop-in of individual
 * sections behind the explosion.
 *
 * On non-low tier it short-circuits to a fragment, so desktop pays no
 * DOM cost for the wrapper and the inner showcase / case-studies
 * lowTierOnly gates already return null anyway.
 *
 * SSR fallback returns `true` for `introActive` so the wrapper renders
 * opacity-0 on the first paint — the PageLoadingScreen splash covers
 * everything underneath while React hydrates, so the user never sees
 * the unfilled stack.
 */
export function MobileIntroGate({ children }: { children: ReactNode }) {
  const { tier } = useQuality();
  const introActive = useSyncExternalStore(
    subscribeIsIntroActive,
    getIsIntroActive,
    () => true,
  );

  if (tier !== 'low') return <>{children}</>;

  return (
    <div
      aria-hidden={introActive}
      className={`transition-[opacity,transform] duration-[1100ms] ease-[var(--ease-feedback)] ${
        introActive
          ? 'pointer-events-none translate-y-6 opacity-0'
          : 'translate-y-0 opacity-100'
      }`}
    >
      {children}
    </div>
  );
}
